import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReportLayout from "@/components/reports/ReportLayout";
import FilterCard from "@/components/reports/FilterCard";
import DueAnalysisTable, {
  DueItem,
} from "@/components/reports/DueAnalysisTable";
import ReportSummaryHeader from "@/components/reports/ReportSummaryHeader";

// Mock due analysis data
const INITIAL_DUE_DATA: DueItem[] = [
  {
    id: 1,
    consumer: "ABC Corporation",
    consumerType: "Commercial",
    service: "Water Supply",
    totalDue: 4850,
    dueDays: 15,
  },
  {
    id: 2,
    consumer: "John Smith",
    consumerType: "Residential",
    service: "Electricity",
    totalDue: 785,
    dueDays: 30,
  },
  {
    id: 3,
    consumer: "XYZ Industries",
    consumerType: "Industrial",
    service: "Waste Management",
    totalDue: 12500,
    dueDays: 45,
  },
  {
    id: 4,
    consumer: "City Hospital",
    consumerType: "Government",
    service: "Water Supply",
    totalDue: 3200,
    dueDays: 10,
  },
  {
    id: 5,
    consumer: "Sarah Johnson",
    consumerType: "Residential",
    service: "Internet",
    totalDue: 350,
    dueDays: 5,
  },
  {
    id: 6,
    consumer: "Downtown Hotel",
    consumerType: "Commercial",
    service: "Electricity",
    totalDue: 7800,
    dueDays: 25,
  },
];

const DueAnalysisPage = () => {
  const [agingPeriod, setAgingPeriod] = useState("all");
  const [consumerType, setConsumerType] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [dueData, setDueData] = useState(INITIAL_DUE_DATA);

  const totalDueAmount = dueData.reduce((sum, item) => sum + item.totalDue, 0);
  const avgDueDays = Math.round(
    dueData.reduce((sum, item) => sum + item.dueDays, 0) / dueData.length
  );

  const handleFilter = () => {
    // In a real app, this would filter based on the selected criteria
    // For this demo, we'll just shuffle the data to simulate filtering
    setDueData([...INITIAL_DUE_DATA].sort(() => Math.random() - 0.5));
  };

  const handleReset = () => {
    setAgingPeriod("all");
    setConsumerType("");
    setServiceType("");
    setDueData(INITIAL_DUE_DATA);
  };

  return (
    <ReportLayout
      title="Due Analysis Report"
      description="Analyze and prioritize overdue collections"
    >
      <FilterCard
        title="Report Filters"
        description="Set parameters to analyze specific dues"
        onReset={handleReset}
        onFilter={handleFilter}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="aging">Aging Period</Label>
            <Select value={agingPeriod} onValueChange={setAgingPeriod}>
              <SelectTrigger>
                <SelectValue placeholder="Select aging period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="0-15">0-15 days</SelectItem>
                <SelectItem value="16-30">16-30 days</SelectItem>
                <SelectItem value="31-60">31-60 days</SelectItem>
                <SelectItem value="61+">61+ days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="consumer-type">Consumer Type</Label>
            <Select value={consumerType} onValueChange={setConsumerType}>
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="Residential">Residential</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Industrial">Industrial</SelectItem>
                <SelectItem value="Government">Government</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-type">Service Type</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger>
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                <SelectItem value="Water Supply">Water Supply</SelectItem>
                <SelectItem value="Electricity">Electricity</SelectItem>
                <SelectItem value="Internet">Internet</SelectItem>
                <SelectItem value="Waste Management">
                  Waste Management
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </FilterCard>

      <Card>
        <CardHeader>
          <ReportSummaryHeader
            title="Due Analysis"
            description={`${
              agingPeriod !== "all"
                ? `Aging: ${agingPeriod} days`
                : "All aging periods"
            }${consumerType && ` • Type: ${consumerType}`}${
              serviceType && ` • Service: ${serviceType}`
            }`}
            totalAmount={totalDueAmount}
            additionalInfo={
              <div className="text-sm text-muted-foreground">
                Avg. {avgDueDays} days overdue
              </div>
            }
          />
        </CardHeader>
        <CardContent>
          <DueAnalysisTable data={dueData} />
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default DueAnalysisPage;
